import React, { useState, useEffect, useRef, useMemo } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

export interface SearchableSelectProps {
  label: string;
  value: string;
  options: SearchableSelectOption[];
  placeholder?: string;
  onChange: (v: string) => void;
}

/** Searchable dropdown — same pattern as subscription invoice / subscription modals. */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  value,
  options,
  placeholder = 'Search…',
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = options.find(o => o.value === value)?.label ?? value;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      o =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <input
        type="text"
        className={inputClass}
        value={open ? query : selectedLabel}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        placeholder={selectedLabel || placeholder}
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute top-full mt-1 z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto"
          style={{ maxHeight: '200px' }}
        >
          {filtered.length > 0 ? (
            filtered.map(o => (
              <button
                key={o.value || '__empty'}
                type="button"
                onMouseDown={() => {
                  onChange(o.value);
                  setQuery('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                {o.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-slate-400">No match</div>
          )}
        </div>
      )}
    </div>
  );
};
